import { Store } from "redux";

import { EntityType } from "@mapmycustomers/shared";

import { FILTER_ENTITY_TYPES } from "@app/component/filtering/utils/consts";
import { ensureSidebarIsClosed } from "@app/component/flows/MapOnboarding/utils";
import type { TourStepAction } from "@app/component/flows/types/TourDescriptor";
import waitForElement from "@app/component/flows/utils/waitForElement";
import i18nService from "@app/config/I18nService";
import localSettings from "@app/config/LocalSettings";
import { selectMapTool } from "@app/scene/map/store/actions";
import { fetchPins } from "@app/scene/map/store/mapMode/actions";
import { getMapViewState } from "@app/scene/map/store/mapMode/selectors";
import { getCurrentUserId } from "@app/store/iam";
import type { Actions, RootState } from "@app/store/rootReducer";
import { selectSavedFilter } from "@app/store/savedFilters/actions";
import type MapPersistedViewportState from "@app/types/map/MapPersistedViewportState";
import companyFieldModel from "@app/util/fieldModel/CompanyFieldModel";
import { isCustomField } from "@app/util/fieldModel/impl/assert";
import CustomField from "@app/util/fieldModel/impl/CustomField";
import { getSimpleConditionForField } from "@app/util/filters/utils";

import { errorMessages } from "./messages";

type TourStore = Store<RootState, Actions>;

// The company field the tour filters on ("posstatus") is org-specific, so we don't hardcode its
// name. What the tour actually needs is a company single-option field offering an "Active" option;
// that is exactly what we detect here and what step 10 verifies the user picked.
const ACTIVE_OPTION_NAME = "Active";

// Owner filters key off the "user" filterName (CompanyFieldName.USER -> CommonFieldName.USER =
// "user"; UserField defaults filterName to its name). Its condition value is an array of user ids.
const OWNER_FILTER_NAME = "user";

// Continental-US framing for the map's initial view (LongLat = [lng, lat]). Zoom 4 shows the
// lower-48. Applied via the persisted viewport, which the Map scene reads on a fresh mount
// (there is no redux action that sets center/zoom — the live viewport is driven imperatively on
// the Google Map ref, which a tour step cannot reach).
const USA_VIEWPORT: MapPersistedViewportState = { center: [-98.35, 39.5], zoom: 4 };

export const delay = (ms: number) => new Promise<void>((resolve) => window.setTimeout(resolve, ms));

const getCompanyActiveOptionField = (): CustomField | undefined =>
  companyFieldModel.fields.find(
    (field): field is CustomField =>
      isCustomField(field) && field.isSingleOptionField && field.displayName === "posstatus"
  );

// Mirrors ToolSelectionPanel's toggle-off path. The tour never enters a tool mode, but a leftover
// tool from prior activity hides #map-filter-button (TopPanel renders null), so clear it before
// waiting for map readiness.
const deselectMapTool = (store: TourStore) => {
  store.dispatch(selectMapTool.request(undefined));
};

// fetchPins is the UI "Clear All" path: applies view settings, refetches, persists metadata. Every
// entity key must be explicitly undefined — the reducer shallow-merges, so `filter: {}` is a no-op.
// Unlike JasperBulkEdit we leave ALL THREE entities visible, so step 2's hide-People-and-Deals
// exercise has something to do; step 2's canGoNext then waits for companies-only.
const prepareMapForTour = (store: TourStore) => {
  store.dispatch(selectSavedFilter({ entityType: EntityType.PIN, savedFilter: undefined }));
  store.dispatch(
    fetchPins.request({
      request: {
        filter: {
          [EntityType.COMPANY]: undefined,
          [EntityType.DEAL]: undefined,
          [EntityType.PERSON]: undefined,
          universal: undefined,
        },
        selectedSavedFilterId: undefined,
        visibleEntities: [EntityType.COMPANY, EntityType.PERSON, EntityType.DEAL],
      },
    })
  );
};

const isMapUnpreparedForTour = (store: TourStore): boolean => {
  const { filter, visibleEntities } = getMapViewState(store.getState());
  return (
    FILTER_ENTITY_TYPES.some((entityType) => !!filter?.[entityType]?.conditions.length) ||
    visibleEntities.length !== 3
  );
};

// The Map scene re-applies persisted view settings over the store on mount, and the persisted
// metadata only updates after an API round-trip. Dispatching before the map has settled gets
// overwritten, so wait for the filter bar plus a settle delay.
const waitForMapReady = () =>
  new Promise<void>((resolve) => {
    waitForElement("#map-filter-button", () => window.setTimeout(resolve, 500), { timeout: 0 });
  });

// Step 1 (before navigating to the Map): hard-require the "Active" option field, seed the USA
// viewport for the upcoming fresh map mount, and close any open record sidebar.
export const initializeAcaTourOnBefore = async (): Promise<TourStepAction | void> => {
  ensureSidebarIsClosed();
  if (!getCompanyActiveOptionField()) {
    return {
      action: "error" as const,
      message: i18nService.formatMessage(errorMessages.noPosStatusField),
    };
  }
  localSettings.updateViewportState(USA_VIEWPORT);
};

// Step 2 (first on-map step): clear filters, reset visibility to all three, close the sidebar.
// Map init may overwrite the prepared state with persisted filters/visibility — verify and retry.
export const prepareMapOnBefore = async ({ store }: { store: TourStore }): Promise<void> => {
  deselectMapTool(store);
  await waitForMapReady();
  prepareMapForTour(store);
  ensureSidebarIsClosed();
  await delay(1000);
  if (isMapUnpreparedForTour(store)) {
    prepareMapForTour(store);
  }
};

export const ensureSidebarClosedOnBefore = async (): Promise<void> => {
  ensureSidebarIsClosed();
};

// let the drawer open animation finish before anchoring the last step's tooltip
export const waitForDrawerOnBefore = async (): Promise<void> => {
  await delay(250);
};

export const isOnlyCompaniesVisible = ({ store }: { store: TourStore }): boolean => {
  const { visibleEntities } = getMapViewState(store.getState());
  return visibleEntities.length === 1 && visibleEntities[0] === EntityType.COMPANY;
};

// The current user is the only selected owner. A condition only reaches the store once complete
// (the popover commits complete filters to redux on a 500ms debounce), so once the value is set we
// can read it here.
export const isOwnerFilterSetToCurrentUser = ({ store }: { store: TourStore }): boolean => {
  const state = store.getState();
  const currentUserId = getCurrentUserId(state);
  if (currentUserId === undefined) {
    return false;
  }
  const companyFilter = getMapViewState(state).filter?.[EntityType.COMPANY];
  const value = getSimpleConditionForField(companyFilter, OWNER_FILTER_NAME)?.value;
  return Array.isArray(value) && value.length === 1 && value[0] === currentUserId;
};

// Exactly the "Active" option of the posstatus field is selected. The stored value is the option's
// numeric id, so resolve it back through the field's own options rather than matching a string.
export const isPosStatusFilterSetToActive = ({ store }: { store: TourStore }): boolean => {
  const field = getCompanyActiveOptionField();
  if (!field) {
    return false;
  }
  const companyFilter = getMapViewState(store.getState()).filter?.[EntityType.COMPANY];
  const value = getSimpleConditionForField(companyFilter, field.filterName)?.value;
  if (!Array.isArray(value) || value.length !== 1) {
    return false;
  }
  const [selected] = value;
  const chosen = (field.customFieldData.options ?? []).find(
    (option) => option.value === selected || option.displayName === selected
  );
  return chosen?.displayName === ACTIVE_OPTION_NAME;
};
